import { ts } from '@shared/lib/diagTs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, type DateRange as RdpRange } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-day-picker/style.css';
import { useDateRange, useSetDateRange, toYmd, type DateRange } from '@shared/state/dateRangeStore';
import { useAccount } from '@shared/state/accountStore';
import { useFirstTrade } from '@features/dashboard/hooks/useFirstTrade';
import { BRAND_GRADIENT } from '@shared/styles/brand';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Diagnostics: demo/dev/local only (prod builds with --mode prod). Traces the Apply-dates flow so we
// can see the selected draft, the committed range, and whether it actually changed → refetch.
// The instance-id + MOUNT/UNMOUNT + per-render draft lines exist to be SURE (not guess) whether an
// "Apply → UNCHANGED" is because the picker REMOUNTED (fresh draft reset to the current range) or the
// preset/selection simply equals the current range: watch for an UNMOUNT+MOUNT (new instance id)
// between "preset →" and "Apply clicked", or a draft that reverts across renders without a remount.
const DATE_DIAG = import.meta.env.MODE !== 'prod';
const ymdOrNull = (d: Date | undefined) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null);
const draftYmd = (r: RdpRange | undefined) => ({ from: ymdOrNull(r?.from), to: ymdOrNull(r?.to) });
let pickerInstanceSeq = 0;

/** Local-midnight Date from a `YYYY-MM-DD` (react-day-picker works in local Dates). */
function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d));
}

function addDays(base: Date, delta: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + delta);
  return d;
}

/**
 * First displayed month, chosen so the range's end (usually today) is VISIBLE: one pane back in the
 * two-up layout, and the end's own month when only one pane is shown — otherwise a single-month
 * picker would open on last month and hide the very day it just selected.
 */
function leftMonthFor(to: Date, months: 1 | 2 = 2): Date {
  return new Date(to.getFullYear(), to.getMonth() - (months === 2 ? 1 : 0), 1);
}

interface CalendarProps {
  /** Initial draft range (`YYYY-MM-DD`). */
  initialFrom: string;
  initialTo: string;
  /** Selectable lower bound (dashboard: the account's first trade; backtester: none). Presets that
   * would start before it are hidden and earlier months can't be paged to. */
  minDate?: Date;
  /**
   * The full span of data behind this picker (`YYYY-MM-DD`). When supplied, a **Max Time** preset
   * is offered next to YTD that selects all of it (2026-07-28 user request: "Max time should select
   * as much history as you have for that product/timeframe"). Omit it and no such preset appears —
   * a caller that doesn't know its own data span cannot honestly offer one.
   */
  maxRange?: DateRange;
  /**
   * How many months to show side by side. Two by default (the Figma). Pass **1** in a narrow
   * container: two months are ~704px wide (7 × `--rdp-day-width` × 2 + `--rdp-months-gap`) and
   * `.journal-rdp .rdp-months` is deliberately `nowrap`, so in the backtester's 352px panel column
   * the second month — the one holding *today* — was clipped out of view entirely (2026-07-29).
   */
  months?: 1 | 2;
  /** Fired on **Apply dates** with the chosen `{from,to}` (to = from when a single day is picked). */
  onApply: (range: DateRange) => void;
  /**
   * Fired on **Cancel** (owner, 2026-10-12). The host closes its popover, which unmounts the calendar
   * and so drops the unapplied draft: the next open starts from the applied range again. No Cancel
   * button is drawn without it.
   */
  onCancel?: () => void;
}

/**
 * Two-month calendar **range** picker (react-day-picker v9), styled to the Figma — gradient range
 * endpoints (rounded) with mint (`--cal-win`) in-between days (see `.journal-rdp` in `tailwind.css`),
 * a row of quick presets (Today / Yesterday / 7D / 30D / This month / MTD / Last 12 month / Last year
 * / YTD), and an **Apply dates** button. Picking on the calendar or a preset stages a *draft* range;
 * **Apply** fires `onApply`. This is the REUSABLE core (2026-07-22, user request: the backtester's
 * test-period selection uses the same dates component); the store-bound dashboard wrapper is
 * `DateRangePicker` below.
 */
export function DateRangeCalendar({
  initialFrom, initialTo, minDate, maxRange, months = 2, onApply, onCancel,
}: CalendarProps) {
  // Paging back is bounded by whichever lower edge exists: the data span when the caller knows it
  // (Max Time), otherwise the account's first trade.
  const lowerBound = maxRange ? parseYmd(maxRange.from) : minDate;
  const minMonth = lowerBound ? new Date(lowerBound.getFullYear(), lowerBound.getMonth(), 1) : undefined;

  const [draft, setDraft] = useState<RdpRange | undefined>(() => ({
    from: parseYmd(initialFrom),
    to: parseYmd(initialTo),
  }));
  const [month, setMonth] = useState<Date>(() => leftMonthFor(parseYmd(initialTo), months));

  // ── Lifecycle trace (demo/dev only) — see the DATE_DIAG note above. A stable per-mount id +
  // render counter make it unambiguous whether a draft "reset" is a REMOUNT or a state revert.
  const instanceId = useRef(0);
  if (instanceId.current === 0) instanceId.current = ++pickerInstanceSeq;
  const renderCount = useRef(0);
  renderCount.current += 1;
  const initialDraft = useRef<{ from: string | null; to: string | null } | null>(null);
  if (initialDraft.current === null) initialDraft.current = draftYmd(draft);
  if (DATE_DIAG) console.log(ts(), '[date-picker] render', {
    instance: instanceId.current, n: renderCount.current, draft: draftYmd(draft),
  });
  useEffect(() => {
    if (DATE_DIAG) console.log(ts(), '[date-picker] MOUNT', {
      instance: instanceId.current, initialDraft: draftYmd(draft),
    });
    return () => { if (DATE_DIAG) console.log(ts(), '[date-picker] UNMOUNT', { instance: instanceId.current }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only
  }, []);

  const presets = useMemo<{ label: string; range: DateRange }[]>(() => {
    const today = new Date();
    const r = (from: Date, to: Date): DateRange => ({ from: toYmd(from), to: toYmd(to) });
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const all = [
      { label: 'Today',         range: r(today, today) },
      { label: 'Yesterday',     range: r(addDays(today, -1), addDays(today, -1)) },
      { label: '7D',            range: r(addDays(today, -6), today) },
      { label: '30D',           range: r(addDays(today, -29), today) },
      { label: 'This month',    range: r(monthStart, today) },
      { label: 'MTD',           range: r(monthStart, today) },
      { label: 'Last 12 month', range: r(new Date(today.getFullYear(), today.getMonth() - 12, today.getDate()), today) },
      { label: 'Last year',     range: r(new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31)) },
      { label: 'YTD',           range: r(new Date(today.getFullYear(), 0, 1), today) },
    ];
    // Hide presets that would start BEFORE the lower bound — a window with no data behind its
    // start is irrelevant (e.g. "Last year" when the earliest trade is a week ago).
    const bounded = minDate ? all.filter(p => parseYmd(p.range.from) >= minDate) : all;
    // "Max Time" is exempt from that filter: it IS the lower bound, so it can never precede it.
    return maxRange ? [...bounded, { label: 'Max Time', range: maxRange }] : bounded;
  }, [minDate, maxRange]);

  function applyPreset(p: DateRange) {
    if (DATE_DIAG) console.log(ts(), '[date-picker] preset →', p);
    const from = parseYmd(p.from);
    const to = parseYmd(p.to);
    setDraft({ from, to });
    setMonth(leftMonthFor(to, months));
  }

  function commit() {
    if (DATE_DIAG) console.log(ts(), '[date-picker] Apply clicked', {
      instance: instanceId.current,
      renders: renderCount.current,
      draft: draftYmd(draft),
      // If false here, the draft never moved off the mount value → the preset/selection didn't stick
      // (or the picker remounted).
      draftMovedSinceMount: JSON.stringify(draftYmd(draft)) !== JSON.stringify(initialDraft.current),
    });
    if (!draft?.from) {
      if (DATE_DIAG) console.warn(ts(), '[date-picker] Apply IGNORED - no draft.from (button should be disabled)');
      return;
    }
    onApply({ from: toYmd(draft.from), to: toYmd(draft.to ?? draft.from) });
  }

  return (
    <div className="journal-rdp flex flex-col gap-6">
      <DayPicker
        mode="range"
        numberOfMonths={months}
        selected={draft}
        onSelect={(r) => { if (DATE_DIAG) console.log(ts(), '[date-picker] calendar select', { from: ymdOrNull(r?.from), to: ymdOrNull(r?.to) }); setDraft(r); }}
        month={minMonth && month < minMonth ? minMonth : month}
        onMonthChange={setMonth}
        startMonth={minMonth}
        showOutsideDays={false}
        weekStartsOn={0}
        // A Max-Time span can legitimately reach further back than `minDate` guards for, and its end
        // can be older than today (a product whose tape stops), so the bounds follow it when given.
        disabled={(() => {
          const lower = maxRange ? parseYmd(maxRange.from) : minDate;
          const upper = maxRange ? parseYmd(maxRange.to) : new Date();
          return lower ? [{ before: lower }, { after: upper }] : { after: upper };
        })()}
        formatters={{ formatWeekdayName: (d) => WEEKDAYS[d.getDay()] ?? '' }}
        components={{
          Chevron: (props) =>
            props.orientation === 'left'
              ? <ChevronLeft className="h-5 w-5" />
              : <ChevronRight className="h-5 w-5" />,
        }}
      />

      {/* Quick presets (Figma) — stage a draft range; Apply commits. Since 2026-10-12 (owner: "a hover
          behavior and a bit more spacing between the preselected dates") each is a soft pill that
          darkens on hover, with wider gaps. The gradient text lives on an inner span: the clip-to-text
          trick spends the element's own background, so the pill colour has to sit on the button. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => applyPreset(p.range)}
            className="rounded-lg bg-canvas-inset px-3 py-1.5 text-[13px] font-semibold transition-colors hover:bg-[var(--canvas-divider-strong)]"
          >
            <span
              style={{
                background: BRAND_GRADIENT,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              {p.label}
            </span>
          </button>
        ))}
      </div>

      {/* PINNED (QA 2026-08-05: "the Apply dates button is below the scroll fold"). In a scrolling
          popover — the composer's test-period picker clamps its height to the space under the
          trigger — this row used to scroll away with the calendar, so nothing told the user a picked
          range still needed applying. `sticky bottom-0` keeps it at the foot of whatever scrollport
          contains it, and is inert where nothing scrolls (the top-bar picker). The wrapper carries
          the card background so scrolled-away weeks don't ghost through behind the button. */}
      <div
        className="sticky bottom-0 z-10 flex justify-end gap-3 pt-2"
        style={{ background: 'var(--canvas-raised)' }}
      >
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center rounded-lg border border-[var(--canvas-divider-strong)] bg-canvas-raised px-4 py-2 text-[13px] font-semibold text-text transition-colors hover:bg-canvas-inset"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={commit}
          disabled={!draft?.from}
          className="inline-flex items-center rounded-lg px-4 py-2 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          style={{ background: BRAND_GRADIENT }}
        >
          Apply dates
        </button>
      </div>
    </div>
  );
}

interface Props {
  /** Called once a range is applied, so the popover can close. */
  onCommit?: () => void;
  /** Called on Cancel, so the popover can close without applying. */
  onCancel?: () => void;
}

/**
 * The dashboard's store-bound wrapper: seeds the calendar from the global `dateRangeStore`, bounds
 * it by the account's first trade, and commits Apply back to the store (queries refetch off it).
 */
export function DateRangePicker({ onCommit, onCancel }: Props) {
  const range = useDateRange();
  const setRange = useSetDateRange();
  const account = useAccount();
  const firstTrade = useFirstTrade({ account: account || undefined });

  // Lower bound = the account's first trade (local midnight). Nothing before it is selectable.
  const firstTradeDate = useMemo(() => {
    const iso = firstTrade.data?.firstTradeAt;
    if (!iso) return undefined;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? undefined : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [firstTrade.data]);

  return (
    <DateRangeCalendar
      initialFrom={range.from}
      initialTo={range.to}
      minDate={firstTradeDate}
      onApply={(next) => {
        const changed = next.from !== range.from || next.to !== range.to;
        if (DATE_DIAG) console.log(ts(), '[date-picker] committing range → store', next, changed ? '(CHANGED → should refetch)' : '(UNCHANGED → no refetch: same as current range)');
        setRange(next);
        onCommit?.();
      }}
      onCancel={onCancel}
    />
  );
}
